import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext/AuthContext';

import { Box, Button, Card, CircularProgress, IconButton, Modal, Stack, Typography, } from '@mui/material';

import { BoostCard, InventoryCard } from './index';

import { BoostDuration } from './components/BoostCard/styles';
import {
  activeBoostCardSx,
  addInventoryCardSx,
  closeButtonSx,
  headerStackSx,
  iconBoxSx,
  infoBodyTextSx,
  infoCardSx,
  infoHeaderStackProps,
  infoTitleTextSx,
  inventoryBlurOverlaySx,
  inventoryEmptyCardSx,
  inventoryEmptyTextSx,
  inventoryRowStackProps,
  inventoryWrapperSx,
  leftRowSx,
  modalCardSx,
  modalContainerSx,
  queueLabelTextSx,
  queueListStackProps,
  queueSectionStackProps,
  titleTextSx,
} from './styles';

import { Briefcase, Info, Plus, Rocket, X } from 'lucide-react';
import PulseLineIcon from '../../components/PulseIcon/PulseIcon';
import getMarginBoosts from './services/get/getMarginBoosts/getMarginBoosts';
import { MarginBoostDTO } from './services/get/getMarginBoosts/models';
import cancelMarginBoost from './services/post/cancelMarginBoost/cancelMarginBoost';
import activateMarginBoost from './services/post/activateMarginBoost/activateMarginBoost';
import expireMarginBoost from './services/post/expireMarginBoost/expireMarginBoost';

interface BoostModalProps {
  open: boolean;
  onClose: () => void;
}

const DURATION_BUCKETS: BoostDuration[] = [2, 4, 8, 12, 24];

const toDurationHours = (seconds: number): BoostDuration => {
  const hours = Math.round(seconds / 3600);
  const valid: BoostDuration[] = [2, 4, 8, 12, 24];
  return (valid.includes(hours as BoostDuration) ? hours : 2) as BoostDuration;
};

const BoostModal: React.FC<BoostModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation('BoostModal');
  const navigate = useNavigate();
  const { user } = useAuth();

  const [boosts, setBoosts] = useState<MarginBoostDTO[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchBoosts = () => {
    if (!user?.userId) return;
    setLoading(true);
    getMarginBoosts(user.userId)
      .then(setBoosts)
      .catch(() => setBoosts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !user?.userId) return;
    fetchBoosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.userId]);

  const handleDelete = (marginBoostId: number) => {
    cancelMarginBoost(
      marginBoostId,
      () => {
        fetchBoosts();
        toast.success(t('TOAST.CANCEL_SUCCESS'));
      },
      () => {
        toast.error(t('TOAST.CANCEL_ERROR'));
      },
    );
  };

  const handleActivate = (marginBoostId: number) => {
    activateMarginBoost(
      marginBoostId,
      () => {
        fetchBoosts();
        toast.success(t('TOAST.ACTIVATE_SUCCESS'));
      },
      () => {
        toast.error(t('TOAST.ACTIVATE_ERROR'));
      },
    );
  };

  const handleExpire = (marginBoostId: number) => {
    expireMarginBoost(
      marginBoostId,
      () => fetchBoosts(),
      (errorMessage) => console.error(errorMessage),
    );
  };

  const activeBoost = boosts.find((b) => b.status === 'ACTIVE') ?? null;

  const getActiveEndTime = (): number | null => {
    if (!activeBoost) return null;
    if (activeBoost.expiresAt) return activeBoost.expiresAt;
    if (activeBoost.executedAt) return activeBoost.executedAt + activeBoost.durationSeconds * 1000;
    return null;
  };
  const activeEndTime = getActiveEndTime();

  const queuedBoosts = boosts.filter((b) => b.status === 'QUEUED');
  const availableBoosts = boosts.filter((b) => b.status === 'PURCHASED');

  const inventoryItems = DURATION_BUCKETS.map((d) => {
    const matching = availableBoosts.filter(
      (b) => toDurationHours(b.durationSeconds) === d,
    );
    return {
      duration: d,
      quantity: matching.length,
      firstId: matching[0]?.marginBoostId ?? null,
    };
  });

  const allEmpty = inventoryItems.every((item) => item.quantity === 0);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalContainerSx} onClick={onClose}>
        <Card sx={modalCardSx} onClick={(e) => e.stopPropagation()}>
          <IconButton
            sx={closeButtonSx}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          >
            <X size={16} color="#CCD4FF" />
          </IconButton>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 10,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Card sx={activeBoostCardSx}>
                <Stack sx={headerStackSx}>
                  <Stack sx={leftRowSx}>
                    <Box sx={iconBoxSx}>
                      <Rocket size={16} color="#CCD4FF" />
                    </Box>

                    <Typography sx={titleTextSx}>
                      {t('CURRENTLY_ACTIVE_BOOST')}
                    </Typography>
                  </Stack>

                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      onClose();
                      navigate('/store');
                    }}
                  >
                    {t('GO_TO_STORE')}
                  </Button>
                </Stack>

                {activeBoost ? (
                  <BoostCard
                    status="active"
                    duration={toDurationHours(activeBoost.durationSeconds)}
                    expiresAt={activeEndTime}
                    onExpire={() => handleExpire(activeBoost.marginBoostId)}
                  />
                ) : (
                  <BoostCard status="empty" />
                )}

                <Stack {...queueSectionStackProps}>
                  <Typography sx={queueLabelTextSx}>{t('QUEUE')}</Typography>

                  <Stack {...queueListStackProps}>
                    {queuedBoosts.length > 0 ? (
                      queuedBoosts.map((boost, index) => {
                        let activatesAt: number | null = null;
                        if (activeEndTime) {
                          activatesAt = activeEndTime;
                          for (let i = 0; i < index; i++) {
                            activatesAt +=
                              queuedBoosts[i].durationSeconds * 1000;
                          }
                        }
                        return (
                          <BoostCard
                            key={boost.marginBoostId}
                            status="waiting"
                            duration={toDurationHours(boost.durationSeconds)}
                            expiresAt={activatesAt}
                            queued
                            onDelete={() => handleDelete(boost.marginBoostId)}
                          />
                        );
                      })
                    ) : (
                      <BoostCard status="empty" queued />
                    )}
                  </Stack>
                </Stack>
              </Card>

              <Card sx={activeBoostCardSx}>
                <Stack sx={leftRowSx}>
                  <Box sx={iconBoxSx}>
                    <Briefcase size={16} color="#CCD4FF" />
                  </Box>

                  <Typography sx={titleTextSx}>{t('MY_BOOSTS')}</Typography>
                </Stack>

                <Box sx={inventoryWrapperSx}>
                  <Stack {...inventoryRowStackProps}>
                    {inventoryItems.map((item) => (
                      <InventoryCard
                        key={item.duration}
                        quantity={item.quantity}
                        duration={item.duration}
                        onClick={() => {
                          if (item.firstId) handleActivate(item.firstId);
                        }}
                      />
                    ))}

                    <Card sx={addInventoryCardSx}>
                      <Plus size={32} color="#FFFFFF" />
                    </Card>
                  </Stack>

                  {allEmpty && (
                    <>
                      <Box sx={inventoryBlurOverlaySx} />
                      <Card sx={inventoryEmptyCardSx}>
                        <PulseLineIcon sx={{ color: '#CCD4FF' }} />
                        <Typography sx={inventoryEmptyTextSx}>
                          {t('BOOST_SLOT_EMPTY_MESSAGE')}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => {
                            onClose();
                            navigate('/store');
                          }}
                        >
                          {t('BUY_MARGIN_BOOST')}
                        </Button>
                      </Card>
                    </>
                  )}
                </Box>
              </Card>

              <Card sx={infoCardSx}>
                <Stack {...infoHeaderStackProps}>
                  <Info size={24} color="#CCD4FF" />
                  <Typography sx={infoTitleTextSx}>
                    {t('WHAT_IS_MARGIN_BOOSTS')}
                  </Typography>
                </Stack>

                <Typography sx={infoBodyTextSx}>
                  {t('INFO_DESCRIPTION')}
                </Typography>
              </Card>
            </>
          )}
        </Card>
      </Box>
    </Modal>
  );
};

export default BoostModal;
